/* eslint-disable no-unused-vars */
import { useRef, useEffect, useContext } from 'react';
import * as S from './DropDownList.styled';
import { ManageCampaignsContext } from 'contexts';
import { useComputed, useSignal } from '@preact/signals-react';
import { CampaignItem } from './CampaignItem';
import { AppItem } from './AppItem';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';
import { Loader } from 'components';
import { saveLocalStorageItem } from 'utils';

const closeDropDown = (e, searchRef, showDropDown$) => {
  const isDeleteSearchIcon = e.target?.closest('.manageCampaignDeleteSearch');
  const isClickInsideSearch = searchRef?.current?.contains(e.target);
  const isClickInsideDropdown = e.target?.closest('.dropdown_base');
  if (!isClickInsideSearch && !isClickInsideDropdown && !isDeleteSearchIcon) {
    showDropDown$.value = false;
  }
};

const createSearchFilter = (search) => (field) => (item) => {
  const value = item?.[field];
  return (
    (typeof value === 'string' && value.toLowerCase().includes(search.toLowerCase())) ||
    (typeof value === 'number' && value.toString().includes(search))
  );
};

export const DropDownList_v2 = ({ searchRef, showDropDown$, searchValue$, selectedCampaigns$ }) => {
  const { data, isLoading, isError, isFetching } =
    saveCampaignServiceAPI.useGetSearchAppsCampaignsQuery();

  const heightOfList$ = useSignal(400);
  const dropDownListRef = useRef();

  const filteredItems$ = useComputed(() => {
    if (!data) return [];
    const search = searchValue$.value.trim();
    const allItems = Object.values(data);
    const filterBySearch = createSearchFilter(search);
    const fieldsToCheck = ['app', 'packageName', 'campaign', '_campaignid'];
    return allItems.filter((item) => fieldsToCheck.some((field) => filterBySearch(field)(item)));
  });

  const firstAppItemIndex$ = useComputed(() => {
    return filteredItems$.value?.find((item) => item.isApp)?.index;
  });

  const firstCampaignItemIndex$ = useComputed(() => {
    return filteredItems$.value?.find((item) => !item.isApp)?.index;
  });

  const isSelectedCampaign = (_campaignid) => selectedCampaigns$.value.has(_campaignid);

  const isSelectedApp = (_campaignidArr) => _campaignidArr.every(isSelectedCampaign);

  const handleClickOnItem = (campaignOrAppData) => {
    const campIdsToUpdate = [];
    if (campaignOrAppData.isApp) {
      campIdsToUpdate.push(...campaignOrAppData.containedIn);
    } else {
      campIdsToUpdate.push(campaignOrAppData._campaignid);
    }
    const newSet = new Set(selectedCampaigns$.value);
    campIdsToUpdate.forEach((_campaignid) => {
      if (newSet.has(_campaignid)) {
        newSet.delete(_campaignid);
      } else {
        newSet.add(_campaignid);
      }
    });
    selectedCampaigns$.value = newSet;
    saveLocalStorageItem({
      key: 'tableState',
      field: 'filteredCampaigns',
      value: Array.from(newSet),
    });
  };

  useEffect(() => {
    let lastScrollTop = window.scrollY;
    const handleClick = (e) => closeDropDown(e, searchRef, showDropDown$);
    const onWheel = (e) => {
      const currentScrollTop = window.scrollY;
      const hasScrolled = currentScrollTop !== lastScrollTop;
      lastScrollTop = currentScrollTop;
      const isWheelInsideDropdown = e.target?.closest('.dropdown_base');
      if (!isWheelInsideDropdown && hasScrolled) {
        searchRef.current.blur();
        showDropDown$.value = false;
      }
    };
    window.addEventListener('click', handleClick);
    window.addEventListener('wheel', onWheel);
    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('wheel', onWheel);
    };
  }, []);

  if (isLoading) {
    return (
      <S.NoResultContainer ref={dropDownListRef} isOpen={true}>
        <Loader.Local delay={0} />
      </S.NoResultContainer>
    );
  }

  if (!filteredItems$.value?.length) {
    return (
      <S.NoResultContainer ref={dropDownListRef} isOpen={true}>
        No matches found
      </S.NoResultContainer>
    );
  }

  return (
    <S.Container ref={dropDownListRef} isOpen={true} className='dropdown_base'>
      <S.MyVirtuoso
        height={heightOfList$.value}
        totalListHeightChanged={(height) => {
          heightOfList$.value = height;
        }}
        fixedItemHeight={56}
        data={filteredItems$.value}
        itemContent={(index, rowData) => {
          if (!rowData.isApp) {
            return (
              <>
                {rowData.index === firstCampaignItemIndex$.value && <S.Title>Campaigns:</S.Title>}
                <CampaignItem
                  rowData={rowData}
                  handleClickOnItem={handleClickOnItem}
                  isActiveCamp={isSelectedCampaign}
                  searchValue$={searchValue$}
                />
              </>
            );
          }
          if (rowData.isApp) {
            return (
              <>
                {rowData.index === firstAppItemIndex$.value && <S.Title>Applications:</S.Title>}
                <AppItem
                  rowData={rowData}
                  handleClickOnItem={handleClickOnItem}
                  isActiveApp={isSelectedApp}
                  searchValue$={searchValue$}
                />
              </>
            );
          }
        }}
      />
    </S.Container>
  );
};
